import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export default function ActionMenu({ onEdit, onDelete, items, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 8 });
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const resolvedItems = Array.isArray(items) && items.length > 0
    ? items
    : [
      onEdit
        ? {
          key: 'edit',
          label: 'Edit',
          onSelect: onEdit,
        }
        : null,
      onDelete
        ? {
          key: 'delete',
          label: 'Delete',
          onSelect: onDelete,
          tone: 'danger',
        }
        : null,
    ].filter(Boolean);

  const hasItems = resolvedItems.length > 0;
  const triggerDisabled = disabled || !hasItems || resolvedItems.every((item) => item.disabled);

  const updateMenuPosition = () => {
    const triggerRect = triggerRef.current?.getBoundingClientRect();

    if (!triggerRect) {
      return;
    }

    const menuWidth = menuRef.current?.offsetWidth || 176;
    const menuHeight = menuRef.current?.offsetHeight || 0;
    const viewportGap = 8;
    const preferredTop = triggerRect.bottom + viewportGap;
    const shouldOpenUp =
      menuHeight > 0 && preferredTop + menuHeight > window.innerHeight - viewportGap;
    const top = shouldOpenUp
      ? Math.max(triggerRect.top - menuHeight - viewportGap, viewportGap)
      : preferredTop;
    const left = Math.min(
      Math.max(triggerRect.right - menuWidth, viewportGap),
      Math.max(window.innerWidth - menuWidth - viewportGap, viewportGap)
    );

    setMenuPosition({
      top,
      left,
    });
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (
        wrapperRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }

      setIsOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleViewportChange = () => updateMenuPosition();

    updateMenuPosition();
    const animationFrame = window.requestAnimationFrame(updateMenuPosition);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      window.cancelAnimationFrame(animationFrame);
    };
  }, [isOpen]);

  if (!hasItems) {
    return null;
  }

  const wrapperStyle = {
    position: 'relative',
    display: 'inline-block',
  };

  const triggerStyle = {
    border: '1px solid #e5e7eb',
    color: '#111827',
    borderRadius: 8,
    width: 32,
    height: 32,
    cursor: triggerDisabled ? 'not-allowed' : 'pointer',
    fontSize: 18,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    opacity: triggerDisabled ? 0.6 : 1,
  };

  const menuStyle = {
    position: 'fixed',
    top: menuPosition.top,
    left: menuPosition.left,
    marginTop: 0,
    background: '#ffffff',
    borderRadius: 10,
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
    zIndex: 9999,
    minWidth: 136,
    maxWidth: 'calc(100vw - 16px)',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
  };

  const menuItemStyle = {
    width: '100%',
    textAlign: 'left',
    padding: '10px 12px',
    border: 'none',
    background: 'transparent',
    color: '#111827',
    cursor: 'pointer',
    fontSize: 13,
    transition: 'background-color 0.2s ease, color 0.2s ease',
  };

  const handleSelect = (item) => {
    if (item.disabled) {
      return;
    }

    setIsOpen(false);

    if (item.onSelect) {
      item.onSelect();
    }
  };

  const menu = isOpen && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={menuRef}
        style={menuStyle}
        className="dropdown action-menu__dropdown"
        onClick={(event) => event.stopPropagation()}
      >
        {resolvedItems.map((item) => (
          <button
            key={item.key || item.label}
            type="button"
            onClick={() => handleSelect(item)}
            disabled={item.disabled}
            className={`action-menu__item dropdown-item${item.tone === 'danger' ? ' action-menu__item--danger' : ''}`}
            style={{
              ...menuItemStyle,
              color: item.tone === 'danger' ? '#dc2626' : '#111827',
              opacity: item.disabled ? 0.6 : 1,
              cursor: item.disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>,
      document.body
    )
    : null;

  return (
    <div ref={wrapperRef} style={wrapperStyle}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();

          if (triggerDisabled) {
            return;
          }

          updateMenuPosition();
          setIsOpen((previous) => !previous);
        }}
        style={triggerStyle}
        className="action-menu__trigger action-button"
        aria-label="Open actions menu"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={triggerDisabled}
      >
        &#8942;
      </button>

      {menu}
    </div>
  );
}
